import Papa, { LocalFile } from 'papaparse';
import { pascalCase } from 'pascal-case';
import md5 from 'md5';
import deepmerge from 'deepmerge';
import { parse, format } from 'date-fns';

const materialPrimaryKey = 'Materialnummer';

const materialKeys = [
  materialPrimaryKey,
  'Materialkurztext',
  'EAN',
  'Lagerware',
  'Basismengeneinheit',
  'Preis',
  'Preiseinheit',
  'Preismenge',
  'Preismengeneinheit',
  'Ausführung',
  'Bestelleinheit',
  'MengeBestelleinheit',
  'SortKeyMaterial',
  'AssetIDBildVariante',
  'AssetIDBildListeVariante',
  'KMAT',
  'AusführungZusatztext',
];

const productPrimaryKey = 'ProduktID';

const productGroupPrimaryKey = 'ProduktgruppeID';

const productGroupKeys = [
  'Produktgruppe',
  productGroupPrimaryKey,
  'BeschreibungProduktgruppe',
  'BeschreibungMarketingProduktgruppe',
  'AnwendungsbereichProduktgruppe',
  'EigenschaftenProduktgruppe',
  'AusführungProduktgruppe',
  'AssetIDBildProduktgruppe',
  'AssetIDBildListeProduktgruppe',
  'URLSicherheitsdatenblattProduktgruppe',
  'URLTechnischesBlattProduktgruppe',
  'URLMontageanleitungProduktgruppe',
  'URLRatgeberProduktgruppe',
  'URLBroschüreProduktgruppe',
  'HierarchieLvl3',
  'HierarchieLvl2',
  'HierarchieLvl1',
  'Index',
];

const productKeys = [
  productPrimaryKey,
  'ProduktBezeichnung',
  'HinweisKurz',
  'Verbrauch',
  'AssetIDBildProdukt',
  'AssetIDBildListeProdukt',
  'SortKeyProdukt',
  ...productGroupKeys,
];

export interface ProductOrMaterial {
  [key: string]: string | number | Record<string, unknown>;
  [productPrimaryKey]: string;
  material: ProductOrMaterialByKey;
}

export interface ProductOrMaterialByKey {
  [key: string]: ProductOrMaterial;
}

interface MaexchenCollectProductsCSV {
  ID: string;
  Materialnummer: string;
  Materialkurztext: string;
  ZeitraumVon: string;
  ZeitraumBis: string;
  Punkte: string;
  Aktiv: string;
}

interface MaexchenRedeemProductVariants {
  ProduktId: string;
  VariantenId: string;
  Variantenbezeichnung: string;
  GueltigVon: string;
  GueltigBis: string;
  Verfuegbar: boolean;
}

interface MaexchenRedeemProductsCSV {
  ProduktId: string;
  SortKey: number;
  Produktname: string;
  Produktbeschreibung: string;
  Eigenschaften: string;
  GueltigBis: string;
  GueltigVon: string;
  PreisMaexchen: string;
  UrlBild: string;
  Verfuegbar: string;
}

export interface MaexchenRedeemProducts {
  ProduktId: string;
  SortKey: number;
  Produktname: string;
  Produktbeschreibung: string;
  Eigenschaften: string;
  GueltigBis: string;
  GueltigVon: string;
  PreisMaexchen: number;
  UrlBild: string;
  Verfuegbar: boolean;
  variants?: Record<string, MaexchenRedeemProductVariants>;
}

export interface MaexchenCollectProducts {
  Materialnummer: string;
  Materialkurztext: string;
  ZeitraumVon: string;
  ZeitraumBis: string;
  Punkte: number;
  Aktiv: boolean;
}

export interface Document {
  MainFolder: string;
  Subfolder: string;
  Typ: string;
  assetId: string;
  Name: string;
  description: string;
  type: string;
  DownloadURL: string;
}

export interface FireSafety {
  Kategorie: string;
  Profilart: string;
  Stärke: string;
  Brandschutz: string;
  Beplankungslage1: string;
  Beplankungslage2: string;
  ProduktID1: string;
  ProduktID2: string;
  FireSafetyID: string;
  Index: number;
}

export interface Seller {
  SellerID: string;
  Kunde: string;
  Ort: string;
  Postleitzahl: string;
  Straße: string;
}

export interface Course {
  Bereich: string;
  Gruppe: string;
  ID: string;
  Name: string;
  LinkBild: string;
  Beschreibung: string;
  Themen: string;
  Ziele: string;
  Termin: string;
  Ort: string;
  Kosten: string;
  LinkAnmeldung: string;
}

export interface Contact {
  Bereich: string;
  Funktion: string;
  Nachname: string;
  Vorname: string;
  Telefon: string;
  Email: string;
  PLZ: string;
  ContactID: string;
  SortKey: number;
}

export interface ProductFinderData {
  DataID: string;
  FinderId: string;
  ProdId: string;
  ProdDesc: string;
  Property1: string;
  Property2: string;
  Property3: string;
  Property4: string;
  Property5: string;
}

// runtime info for checks
enum ProductFinderConfigPropertyIdPossibilities {
  'Property1',
  'Property2',
  'Property3',
  'Property4',
  'Property5',
}

type ProductFinderConfigPropertyId =
  keyof typeof ProductFinderConfigPropertyIdPossibilities;

// predicate
function isProductFinderConfigPropertyId(
  possibleId: string,
): possibleId is ProductFinderConfigPropertyId {
  return Object.keys(ProductFinderConfigPropertyIdPossibilities).includes(
    possibleId,
  );
}

interface ProductFinderConfigCSV {
  FinderId: string;
  FinderText: string;
  PropertyId: ProductFinderConfigPropertyId;
  PropertyDesc: string;
  PropertyValueSort: string;
  PropertyValueId: string;
  PropertyValue: string;
  PropertyNoteText: string;
  Deactivate: string;
}

interface ProductFinderConfigPropertyValues {
  PropertyValueSort: number;
  PropertyValueId: string;
  PropertyValue: string;
  PropertyNoteText: string;
  Deactivate: string;
}

interface ProductFinderConfigProperty {
  PropertyId: string;
  PropertyDesc: string;
  PropertyValues: Record<string, ProductFinderConfigPropertyValues>;
}

export interface ProductFinderConfig
  extends Partial<
    Record<ProductFinderConfigPropertyId, ProductFinderConfigProperty>
  > {
  FinderId: string;
  FinderText: string;
}

const pascalCaseWithoutLoDash = (header: string) =>
  pascalCase(header).replace(/_/g, '');

const parseFile = <T>(
  fileData: LocalFile,
  headerConverter?: (header: string) => string,
): Promise<T[]> =>
  new Promise<T[]>((res, rej) =>
    Papa.parse(fileData, {
      error: rej,
      complete: ({ data }: { data: T[] }) => res(data),
      encoding: 'utf8',
      header: true,
      delimiter: ';',
      skipEmptyLines: true,
      transformHeader: (header: string) => {
        if (headerConverter) {
          return headerConverter(header);
        }
        return header
          .replace(/[_\- ]/g, '')
          .replace('Hinweiskurz', 'HinweisKurz');
      },
      transform: (
        value: string | number | Record<string, T>,
        header: string,
      ): string | number | Record<string, T> => {
        if (header.startsWith('SortKey')) return Number(value);

        return value;
      },
    }),
  );

const filterObjectByKeys = (
  obj: ProductOrMaterial,
  keys: string[],
): ProductOrMaterial =>
  keys.reduce(
    (assembledObject, currentKey) => ({
      ...assembledObject,
      [currentKey]: obj[currentKey],
    }),
    {} as ProductOrMaterial,
  );

function getSortedArray(origData: string) {
  const sortedOrigLines = origData
    .trim()
    .split('\n')
    .map(entry => entry.trim())
    .filter(entry => !!entry)
    .sort((a, b) => a.localeCompare(b));

  // remove order prefix:
  return sortedOrigLines.map(entry => entry.substring(3));
}

export const getProductsStructure = async (
  csvData: LocalFile,
): Promise<ProductOrMaterialByKey> => {
  const data = await parseFile<ProductOrMaterial>(csvData);

  const productPerId = data.reduce((acc, currentRowEntry, currentIndex) => {
    const ret = { ...acc };
    const currentKey = currentRowEntry[productPrimaryKey];
    const materialEntry = filterObjectByKeys(currentRowEntry, materialKeys);
    const materialImages = getSortedArray(
      materialEntry.AssetIDBildVariante.toString(),
    );
    materialEntry.AssetIDBildListeVariante = materialImages.join('\n');

    // backward compatibility
    materialEntry.AssetIDBildVariante = materialImages[0] ?? '';

    if (ret[currentKey]) {
      // entry already there, add material data
      const materialKey = materialEntry[materialPrimaryKey] as string;
      ret[currentKey].material[materialKey] = materialEntry;
    } else {
      ret[currentKey] = filterObjectByKeys(currentRowEntry, productKeys);
      ret[currentKey].Index = currentIndex;
      const productImages = getSortedArray(
        ret[currentKey].AssetIDBildProdukt.toString(),
      );

      ret[currentKey].AssetIDBildProdukt = productImages[0] ?? '';
      ret[currentKey].AssetIDBildListeProdukt = productImages.join('\n');

      const productGroupImages = getSortedArray(
        ret[currentKey].AssetIDBildProduktgruppe.toString(),
      );
      ret[currentKey].AssetIDBildProduktgruppe = productGroupImages[0] ?? '';
      ret[currentKey].AssetIDBildListeProduktgruppe =
        productGroupImages.join('\n');

      ret[currentKey].material = {
        [materialEntry[materialPrimaryKey] as string]: materialEntry,
      };
    }
    return ret;
  }, {} as ProductOrMaterialByKey);
  return productPerId;
};

export const getDocumentsStructure = async (
  csvData: LocalFile,
): Promise<Record<string, Document>> => {
  const data = await parseFile<Document>(csvData);
  return data.reduce(
    (acc, currentRowEntry: Document) => ({
      ...acc,
      [currentRowEntry.assetId]: currentRowEntry,
    }),
    {},
  );
};

export const getCourseStructure = async (
  csvData: LocalFile,
): Promise<Record<string, Course>> => {
  const data = await parseFile<Course>(csvData);
  return data.reduce(
    (acc, currentRowEntry: Course) => ({
      ...acc,
      [currentRowEntry.ID]: currentRowEntry,
    }),
    {},
  );
};

async function getStructureWithGeneratedId<T>(
  csvData: LocalFile,
  idEntryName: keyof T,
  headerConverter?: (header: string) => string,
  indexEntry?: keyof T,
): Promise<Record<string, T>> {
  type CSVType = Omit<T, typeof idEntryName>;
  const data = await parseFile<CSVType>(csvData, headerConverter);
  return data.reduce((acc, currentRowEntry: CSVType, index) => {
    const id = md5(JSON.stringify(currentRowEntry));
    return {
      ...acc,
      [id]: {
        ...currentRowEntry,
        [idEntryName]: id,
        ...(indexEntry ? { [indexEntry]: index } : {}),
      },
    };
  }, {});
}

function reformatDate(orig: string) {
  return format(parse(orig, 'dd.MM.yyyy', new Date()), 'yyyy-MM-dd');
}

export const getMaexchenCollectProductsStructure = async (
  csvData: LocalFile,
): Promise<Record<string, MaexchenCollectProducts>> => {
  const data = await getStructureWithGeneratedId<MaexchenCollectProductsCSV>(
    csvData,
    'ID',
    pascalCaseWithoutLoDash,
  );
  return Object.values(data).reduce(
    (acc, currentRowEntry: MaexchenCollectProductsCSV) => ({
      ...acc,
      [currentRowEntry.ID]: {
        ...currentRowEntry,
        ZeitraumVon: reformatDate(currentRowEntry.ZeitraumVon),
        ZeitraumBis: reformatDate(currentRowEntry.ZeitraumBis),
        Punkte: Number(currentRowEntry.Punkte),
        Aktiv: currentRowEntry.Aktiv.toLowerCase().trim() === 'ja',
      },
    }),
    {},
  );
};

export const getMaexchenRedeemProductsStructure = async (
  productCsvData: LocalFile,
  productVariantCsvData: LocalFile,
): Promise<Record<string, MaexchenRedeemProducts>> => {
  const variantRawData = await parseFile<MaexchenRedeemProductVariants>(
    productVariantCsvData,
    pascalCaseWithoutLoDash,
  );

  const variantDataByProduct = variantRawData.reduce(
    (acc, currentRowEntry: MaexchenRedeemProductVariants) => {
      const productEntry = acc.get(currentRowEntry.ProduktId);
      const convertedEntry = {
        ...currentRowEntry,
        GueltigVon: reformatDate(currentRowEntry.GueltigVon),
        GueltigBis: reformatDate(currentRowEntry.GueltigBis),
        Verfuegbar:
          String(currentRowEntry.Verfuegbar).toLowerCase().trim() === 'ja',
      };
      if (productEntry) {
        productEntry[currentRowEntry.VariantenId] = convertedEntry;
      } else {
        acc.set(currentRowEntry.ProduktId, {
          [currentRowEntry.VariantenId]: convertedEntry,
        });
      }
      return acc;
    },
    new Map<string, Record<string, MaexchenRedeemProductVariants>>(),
  );

  const data = await parseFile<MaexchenRedeemProductsCSV>(
    productCsvData,
    pascalCaseWithoutLoDash,
  );
  return data.reduce(
    (acc, currentRowEntry: MaexchenRedeemProductsCSV) => ({
      ...acc,
      [currentRowEntry.ProduktId]: {
        ...currentRowEntry,
        PreisMaexchen: Number(currentRowEntry.PreisMaexchen),
        GueltigVon: reformatDate(currentRowEntry.GueltigVon),
        GueltigBis: reformatDate(currentRowEntry.GueltigBis),
        Verfuegbar: currentRowEntry.Verfuegbar.toLowerCase().trim() === 'ja',
        variants: variantDataByProduct.get(currentRowEntry.ProduktId) ?? null,
      },
    }),
    {},
  );
};

export const getFireSafetyStructure = async (
  csvData: LocalFile,
): Promise<Record<string, FireSafety>> =>
  getStructureWithGeneratedId<FireSafety>(
    csvData,
    'FireSafetyID',
    undefined,
    'Index',
  );

export const getSellersStructure = async (
  csvData: LocalFile,
): Promise<Record<string, Seller>> =>
  getStructureWithGeneratedId<Seller>(csvData, 'SellerID');

export const getContactsStructure = async (
  csvData: LocalFile,
): Promise<Record<string, Contact>> =>
  getStructureWithGeneratedId<Contact>(csvData, 'ContactID');

const getProductFinderConfigStructure = async (
  csvData: LocalFile,
): Promise<Record<string, ProductFinderConfig>> => {
  // TODO: implement
  const configRawData = await parseFile<ProductFinderConfigCSV>(
    csvData,
    pascalCaseWithoutLoDash,
  );
  const configData = configRawData.reduce(
    (acc: Record<string, ProductFinderConfig>, next) => {
      const propertyId = pascalCaseWithoutLoDash(next.PropertyId);

      if (!isProductFinderConfigPropertyId(propertyId))
        throw new Error(
          `property id must be one of ${Object.keys(
            ProductFinderConfigPropertyIdPossibilities,
          ).join(',')}`,
        );

      const entry: Record<string, ProductFinderConfig> = {
        [next.FinderId]: {
          FinderId: next.FinderId,
          FinderText: next.FinderText,
          [propertyId]: {
            PropertyId: propertyId,
            PropertyDesc: next.PropertyDesc,
            PropertyValues: {
              [next.PropertyValueId]: {
                PropertyValueId: next.PropertyValueId,
                PropertyValue: next.PropertyValue,
                PropertyValueSort: Number(next.PropertyValueSort),
                PropertyNoteText: next.PropertyNoteText,
                Deactivate: next.Deactivate,
              },
            },
          },
        },
      };
      return deepmerge(acc, entry);
    },
    {},
  );
  return configData;
};

const getProductFinderDataStructure = async (
  csvData: LocalFile,
): Promise<Record<string, ProductFinderData>> =>
  getStructureWithGeneratedId<ProductFinderData>(csvData, 'DataID', header =>
    pascalCaseWithoutLoDash(header),
  );

export const getProductFinderStructure = async (
  configCsvData: LocalFile,
  dataCsvData: LocalFile,
): Promise<{
  config: Record<string, ProductFinderConfig>;
  data: Record<string, ProductFinderData>;
}> => ({
  config: await getProductFinderConfigStructure(configCsvData),
  data: await getProductFinderDataStructure(dataCsvData),
});
